import {QueryErrorResetBoundary} from '@tanstack/react-query';
import router, {NextRouter, useRouter} from 'next/router';
import {Component, ErrorInfo, ReactNode} from 'react';
import {ApiStatusCodeToErrorMessage} from '../common/ApiStatusCodeToErrorMessage.const';
import {SCHEME_CLOSE_WEBVIEW} from '../common/constant';
import {t, tt} from '../common/getTranslations';
import {requestContext} from '../common/requestContext';
import {ApiStatusCodeVo as BillingApiStatusCodeVo} from '../common/shared/api/billing/enums/ApiStatusCodeVo.enum';
import {ApiStatusCodeVo as CmsApiStatusCodeVo} from '../common/shared/api/cms/vo/enums/ApiStatusCodeVo.enum';
import {ApiStatusCodeVo} from '../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {twMerge} from '../common/twMerge';
import {ErrorArea} from '../design-system/organisms/ErrorArea/ErrorArea';
import {showLegacyVersionUpdateDialog} from '../dialog/legacyVersionUpdateDialog';
import {showSimpleDialog} from '../dialog/simpleDialogHandler';
import {logoutPageweb} from '../domain/login/util/logoutPageweb';
import {handleLoginPageweb} from '../user/handleLoginPageweb.function';
import {CmsPreviewError} from './CmsPreviewError';
import {SHOW_NETWORK_ERROR_LIST} from './constants';
import {getSerializedPagewebError} from './getSerializedPagewebError.function';
import {handleRestrictedCountryDeniedError} from './handleRestrictionCountryDeniedError';
import {ItemNotSaleError} from './ItemNotSaleError';
import {NetworkError} from './NetworkError';
import {PagewebErrorType} from './PagewebErrorType.enum';
import {SerializedPagewebError} from './SerializedPagewebError.interface';
import {UnknownError} from './UnknownError';

interface Props {
  reset: VoidFunction;
  refreshOnRetry?: boolean;
  router: NextRouter;
  children: ReactNode;
  className?: string;
}

interface State {
  shouldHandleError: boolean;
  shouldRethrow: boolean;
  initialized: boolean;
  /**
   * rethrow 할 에러
   */
  error?: Error;
  serializedPagewebError?: SerializedPagewebError;
}

/**
 * 처리되지 않은 API Error 를 처리하는 바운더리입니다
 * 네트워크 연결 에러, 요청 처리 실패 등의 에러를 처리합니다
 * 페이지 전체 에러를 보여줘야하는 경우(예: 서버점검) 상위 ErrorBoundary 로 전달합니다
 */
class _ApiErrorBoundary extends Component<Props, State> {
  public state: State = {
    shouldHandleError: false,
    initialized: true,
    shouldRethrow: false,
    error: undefined,
  };

  public static getDerivedStateFromError(error: Error): State {
    const serializedPagewebError = getSerializedPagewebError(error);
    if (
      (serializedPagewebError.type === PagewebErrorType.NicolasApiStatusError ||
        serializedPagewebError.type ===
          PagewebErrorType.KarinaApiStatusError) &&
      serializedPagewebError.resultCode === ApiStatusCodeVo.ServerMaintenance
    ) {
      return {
        shouldHandleError: false,
        initialized: false,
        shouldRethrow: true,
        error,
      };
    } else {
      return {
        shouldHandleError: true,
        initialized: false,
        shouldRethrow: false,
        serializedPagewebError,
      };
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // TODO: 적절한 로깅 처리
    console.error('Uncaught error:', error, errorInfo);
  }

  clearHandleError = () => {
    if (this.state.shouldHandleError) {
      this.setState({shouldHandleError: false});
    }
  };

  initialize = () => {
    if (!this.state.initialized) {
      this.setState({initialized: true});
    }
  };

  public componentDidMount() {
    this.props.router.events.on('routeChangeStart', this.clearHandleError);
    this.props.router.events.on('routeChangeComplete', this.initialize);
  }

  public componentWillUnmount() {
    this.props.router.events.off('routeChangeStart', this.clearHandleError);
    this.props.router.events.off('routeChangeComplete', this.initialize);
  }

  private handleRetry() {
    this.props.reset();
    this.setState({
      shouldHandleError: false,
      initialized: true,
    });
  }

  public render() {
    if (this.state.shouldRethrow) {
      throw this.state.error;
    }
    if (!this.state.shouldHandleError && this.state.initialized) {
      return this.props.children;
    }
    // 에러 정보가 초기화되기 전까지의 약간의 공백 동안에는 자식 요소 렌더링과 에러 처리를 모두 하지 않습니다.
    if (!this.state.shouldHandleError && !this.state.initialized) {
      return null;
    }

    const {type, resultCode, message, originalMessage} =
      this.state.serializedPagewebError || {};
    const {isMobile, isWebView} = requestContext.requestInfo.display;

    if (
      isWebView &&
      (resultCode === ApiStatusCodeVo.NeedUpdate ||
        resultCode === BillingApiStatusCodeVo.NeedUpdate)
    ) {
      showLegacyVersionUpdateDialog();
      return null;
    }

    async function errorShowServerMsgHandler() {
      if (originalMessage?.match(/^.*(로그인이 제한).*/)) {
        await logoutPageweb({
          success: () => window.location.assign('/'),
        });
      } else {
        router.back();
      }
    }

    async function rejectRegisterFor7DaysAfterUnRegisterHandler() {
      if (isWebView) {
        window.location.assign(SCHEME_CLOSE_WEBVIEW);
      } else {
        await logoutPageweb({
          success: () => window.location.assign('/'),
        });
      }
    }

    switch (resultCode) {
      case ApiStatusCodeVo.ItemNotSales:
      case ApiStatusCodeVo.NotAvailableOnWeb:
        return (
          <ItemNotSaleError
            isMobile={isMobile}
            onClickGoToHome={() => {
              window.location.assign('/');
            }}
          />
        );
      case ApiStatusCodeVo.NotAuthorized:
      case ApiStatusCodeVo.ExpiredVerification:
        return (
          <ErrorArea isMobile={isMobile}>
            <ErrorArea.Title>
              {t('alert_message_for_not_logged_in_user')}
            </ErrorArea.Title>
            {!isWebView && (
              <ErrorArea.LabelButton
                onClick={() => {
                  handleLoginPageweb();
                }}
              >
                {t('common_login_button')}
              </ErrorArea.LabelButton>
            )}
          </ErrorArea>
        );
      case ApiStatusCodeVo.KakaoError:
        return (
          <ErrorArea isMobile={isMobile}>
            <ErrorArea.Title>
              {tt(ApiStatusCodeToErrorMessage[ApiStatusCodeVo.KakaoError])}
            </ErrorArea.Title>
          </ErrorArea>
        );
      case ApiStatusCodeVo.ItemAccessRestrictionCountryDenied: {
        handleRestrictedCountryDeniedError(
          this.state.serializedPagewebError as SerializedPagewebError
        );
        return null;
      }
      case ApiStatusCodeVo.NoMappingDataError:
        return (
          <ErrorArea isMobile={isMobile}>
            <ErrorArea.Title>
              {tt(
                ApiStatusCodeToErrorMessage[ApiStatusCodeVo.NoMappingDataError]
              )}
            </ErrorArea.Title>
          </ErrorArea>
        );
      case ApiStatusCodeVo.ErrorShowServerMsg: {
        showSimpleDialog({
          title: '',
          description: message,
          isMobile: requestContext.requestInfo.display.isMobile,
          okButton: {
            type: 'label',
            text: t('common_confirm_button'),
            onClick: errorShowServerMsgHandler,
            closeAfterClick: true,
          },
          onClose: errorShowServerMsgHandler,
        });
        return null;
      }
      case ApiStatusCodeVo.RejectRegisterFor7DaysAfterUnRegister: {
        showSimpleDialog({
          title: '',
          description: message,
          isMobile: requestContext.requestInfo.display.isMobile,
          okButton: {
            type: 'label',
            text: t('common_confirm_button'),
            onClick: rejectRegisterFor7DaysAfterUnRegisterHandler,
            closeAfterClick: true,
          },
          onClose: rejectRegisterFor7DaysAfterUnRegisterHandler,
        });
        return null;
      }
      case ApiStatusCodeVo.SetNotOnSale:
        return (
          <ErrorArea isMobile={isMobile}>
            <ErrorArea.Title>
              {t('sethome_set_not_on_sale_title')}
            </ErrorArea.Title>
            <ErrorArea.DetailText>
              {t('sethome_set_not_on_sale_info')}
            </ErrorArea.DetailText>
            <ErrorArea.LabelButton onClick={() => window.location.assign('/')}>
              {t('contenthome_home_button')}
            </ErrorArea.LabelButton>
          </ErrorArea>
        );
      case ApiStatusCodeVo.BillingMaintenance:
        // 서버 에러 메시지 그대로 노출 + 재시도 버튼 노출
        return (
          <ErrorArea isMobile={isMobile}>
            <ErrorArea.Title>{message}</ErrorArea.Title>
            <ErrorArea.LabelButton
              onClick={() => {
                if (this.props.refreshOnRetry) {
                  window.location.reload();
                } else {
                  this.handleRetry();
                }
              }}
            >
              {t('error_retry_button')}
            </ErrorArea.LabelButton>
          </ErrorArea>
        );
      default: {
        if (
          type === PagewebErrorType.CmsApiStatusError &&
          resultCode === CmsApiStatusCodeVo.ExpiredPreviewViewerToken
        ) {
          return <CmsPreviewError isMobile={isMobile} />;
        } else if (type && SHOW_NETWORK_ERROR_LIST.includes(type)) {
          return (
            <NetworkError
              isMobile={isMobile}
              onClickRetry={() => {
                if (this.props.refreshOnRetry) {
                  window.location.reload();
                } else {
                  this.handleRetry();
                }
              }}
              className={twMerge(
                'bg-theme-bg-base',
                isMobile ? 'min-h-200pxr' : 'min-h-364pxr',
                this.props.className
              )}
            />
          );
        } else {
          return (
            <UnknownError
              isMobile={requestContext.requestInfo.display.isMobile}
              onClickRetry={() => {
                if (this.props.refreshOnRetry) {
                  window.location.reload();
                } else {
                  this.handleRetry();
                }
              }}
              className={twMerge(
                'bg-theme-bg-base',
                isMobile ? 'min-h-200pxr' : 'min-h-364pxr',
                this.props.className
              )}
            />
          );
        }
      }
    }
  }
}

export function ApiErrorBoundary({
  children,
  ...props
}: {
  refreshOnRetry?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const router = useRouter();

  return (
    <QueryErrorResetBoundary>
      {({reset}) => (
        <_ApiErrorBoundary {...props} router={router} reset={reset}>
          {children}
        </_ApiErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
